import { useMemo, useState } from "react"
import { Label, Pie, PieChart, Sector } from "recharts"
import { CreditCard, Banknote } from "lucide-react"

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "./card"
import {
    ChartContainer,
    ChartStyle,
    ChartTooltip,
    ChartTooltipContent,
} from "./chart"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./select"

export const description = "An interactive payment split pie chart"

const defaultChartConfig = {
    cash: {
        label: "Cash",
        color: "#3b82f6",
    },
    card: {
        label: "Card",
        color: "#017971",
    },
}

/**
 * @param {Object} props
 * @param {number} props.cashAmount
 * @param {number} props.cardAmount
 * @param {string} [props.title="Payment Split"]
 * @param {string} [props.description="Cash vs Card payments"]
 */
export function ChartPieInteractive({ 
    cashAmount, 
    cardAmount, 
    title = "Payment Split",
    description = "Cash vs Card payments"
}) {
    const id = "pie-interactive"
    
    const paymentData = [
        { 
            type: "cash", 
            value: cashAmount, 
            label: "Cash", 
            fill: "#3b82f6",
            icon: <Banknote className="h-3 w-3" />
        },
        { 
            type: "card", 
            value: cardAmount, 
            label: "Card", 
            fill: "#017971",
            icon: <CreditCard className="h-3 w-3" />
        },
    ]
    
    const [activeType, setActiveType] = useState(paymentData[0].type)
    const activeIndex = useMemo(
        () => paymentData.findIndex((item) => item.type === activeType),
        [activeType]
    )
    
    const types = useMemo(() => paymentData.map((item) => item.type), [paymentData])
    
    const chartConfig = {
        ...defaultChartConfig,
        payment: {
            label: "Payment",
        },
    }

    return (
        <Card data-chart={id} className="flex flex-col">
            <ChartStyle id={id} config={chartConfig} />
            <CardHeader className="flex-row items-start space-y-0 pb-0">
                <div className="grid gap-1">
                    <CardTitle>{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                </div>
                <Select value={activeType} onValueChange={setActiveType}>
                    <SelectTrigger
                        className="ml-auto h-7 w-[130px] rounded-lg pl-2.5"
                        aria-label="Select payment type">
                        <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent align="end" className="rounded-xl">
                        {types.map((type) => {
                            const dataItem = paymentData.find(item => item.type === type)
                            const config = chartConfig[type]

                            if (!config || !dataItem) {
                                return null
                            }

                            return (
                                <SelectItem
                                    key={type}
                                    value={type}
                                    className="rounded-lg [&_span]:flex"
                                >
                                    <div className="flex items-center gap-2 text-xs">
                                        <span
                                            className="flex h-3 w-3 shrink-0 rounded-xs"
                                            style={{
                                                backgroundColor: dataItem.fill,
                                            }}
                                        />
                                        {dataItem.icon}
                                        {dataItem.label}
                                    </div>
                                </SelectItem>
                            )
                        })}
                    </SelectContent>
                </Select>
            </CardHeader>
            <CardContent className="flex flex-1 justify-center pb-0">
                <ChartContainer
                    id={id}
                    config={chartConfig}
                    className="mx-auto aspect-square w-full max-w-[300px]"
                >
                    <PieChart>
                        <ChartTooltip
                            cursor={false}
                            content={<ChartTooltipContent hideLabel />}
                        />
                        <Pie
                            data={paymentData}
                            dataKey="value"
                            nameKey="label"
                            innerRadius={60}
                            strokeWidth={5}
                            activeIndex={activeIndex}
                            activeShape={({ outerRadius = 0, ...props }) => (
                                <g>
                                    <Sector {...props} outerRadius={outerRadius + 10} />
                                    <Sector
                                        {...props}
                                        outerRadius={outerRadius + 25}
                                        innerRadius={outerRadius + 12}
                                    />
                                </g>
                            )}
                        >
                            <Label
                                content={({ viewBox }) => {
                                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                                        const activeData = paymentData[activeIndex]
                                        const total = paymentData.reduce((sum, item) => sum + item.value, 0)
                                        const percentage = total > 0 ? (activeData.value / total * 100).toFixed(1) : "0"
                                        
                                        return (
                                            <text
                                                x={viewBox.cx}
                                                y={viewBox.cy}
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                            >
                                                <tspan
                                                    x={viewBox.cx}
                                                    y={viewBox.cy}
                                                    className="fill-foreground text-3xl font-bold"
                                                >
                                                    {activeData.value.toLocaleString()}
                                                </tspan>
                                                <tspan
                                                    x={viewBox.cx}
                                                    y={(viewBox.cy || 0) + 24}
                                                    className="fill-muted-foreground"
                                                >
                                                    {percentage}% of total
                                                </tspan>
                                            </text>
                                        )
                                    }
                                }}
                            />
                        </Pie>
                    </PieChart>
                </ChartContainer>
            </CardContent>
            <CardContent className="pt-0">
                <div className="space-y-2">
                    {paymentData.map((item) => {
                        const total = paymentData.reduce((sum, i) => sum + i.value, 0)
                        const percentage = total > 0 ? (item.value / total * 100).toFixed(1) : "0"
                        
                        return (
                            <div key={item.type} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div 
                                        className="h-3 w-3 rounded-xs"
                                        style={{ backgroundColor: item.fill }}
                                    />
                                    {item.icon}
                                    <span className="text-sm">{item.label}</span>
                                </div>
                                <div className="text-right">
                                    <div className="font-medium">{item.value.toLocaleString()}</div>
                                    <div className="text-xs text-muted-foreground">{percentage}%</div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </CardContent>
        </Card>
    )
}
